import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, patch, post, fmt, parseBhd, ApiError, session } from '../../lib/api';

export default function Products() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState('');

  const list = useQuery({
    queryKey: ['products', q, page],
    queryFn: () => get<any>(`/api/v1/products?page=${page}&pageSize=25${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  });

  const detail = useQuery({
    queryKey: ['product', open],
    queryFn: () => get<any>(`/api/v1/products/${open}`),
    enabled: !!open,
  });

  const save = useMutation({
    mutationFn: (v: { id: string; body: any }) => patch(`/api/v1/admin/variants/${v.id}`, v.body),
    onSuccess: () => { setError(''); void qc.invalidateQueries({ queryKey: ['product', open] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const assignBarcode = useMutation({
    mutationFn: (id: string) => post(`/api/v1/admin/variants/${id}/assign-barcode`),
    onSuccess: () => { setError(''); void qc.invalidateQueries({ queryKey: ['product', open] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not allocate a barcode'),
  });

  const canAdjustStock = session.has('inventory.adjust');
  const adjustStock = useMutation({
    mutationFn: (v: { sku: string; storeCode: string; onHand: number }) =>
      post<any>('/api/v1/admin/stock/adjust', { ...v, reason: 'Back-office correction' }),
    onSuccess: () => { setError(''); void qc.invalidateQueries({ queryKey: ['product', open] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Stock update failed'),
  });

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <h1 className="text-xl font-semibold">Products</h1>

      <div className="flex gap-2">
        <input className="input flex-1 max-w-md" placeholder="Search by name or style code…"
          value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      </div>

      {error && <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-2">{error}</div>}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr>
            <th className="th">Product</th><th className="th">Style</th>
            <th className="th">Line</th><th className="th">Season</th>
            <th className="th">Category</th><th className="th">Source</th><th className="th"></th>
          </tr></thead>
          <tbody>
            {list.data?.items?.map((p: any) => (
              <tr key={p.id}>
                <td className="td">
                  <div className="flex items-center gap-2">
                    {p.imageUrl && <img src={p.imageUrl} className="w-8 h-10 object-cover rounded bg-gray-800" alt="" />}
                    <div className="min-w-0">
                      <div className="truncate max-w-xs">{p.titleEn}</div>
                      {p.titleAr && <div className="text-[11px] text-gray-500 truncate" dir="rtl">{p.titleAr}</div>}
                    </div>
                  </div>
                </td>
                <td className="td font-mono text-xs">{p.styleCode ?? '—'}</td>
                <td className="td">{p.line ?? '—'}</td>
                <td className="td">{p.seasonYear ?? '—'}</td>
                <td className="td text-gray-400">{p.category || <span className="text-amber-500/70 text-xs">none</span>}</td>
                <td className="td"><span className="badge bg-gray-800 text-gray-400">{p.source}</span></td>
                <td className="td">
                  <button className="text-xs text-brand hover:underline"
                    onClick={() => setOpen(open === p.id ? null : p.id)}>
                    {open === p.id ? 'Close' : 'Variants'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 flex items-center justify-between border-t border-gray-800 text-xs text-gray-400">
          <span>{list.data?.total ?? 0} products</span>
          <div className="flex gap-2">
            <button className="btn-ghost text-xs py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
            <span className="py-1">page {page}</span>
            <button className="btn-ghost text-xs py-1"
              disabled={(list.data?.items?.length ?? 0) < 25} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>

      {open && detail.data && (
        <div className="card">
          <div className="px-4 py-2 border-b border-gray-800 text-sm font-medium">
            {detail.data.product.titleEn} — size grid
          </div>
          <table className="w-full text-sm">
            <thead><tr>
              <th className="th">SKU</th><th className="th">Size</th><th className="th">Barcode</th>
              <th className="th">Price (BHD, inc. VAT)</th><th className="th">Stock</th><th className="th"></th>
            </tr></thead>
            <tbody>
              {detail.data.variants.map((v: any) => (
                <tr key={v.id}>
                  <td className="td font-mono text-xs">{v.sku}</td>
                  <td className="td">{v.size ?? '—'}</td>
                  <td className="td">
                    {v.barcode
                      ? <span className="font-mono text-xs">{v.barcode}</span>
                      : (
                        <button className="text-xs text-amber-400 hover:underline"
                          onClick={() => assignBarcode.mutate(v.id)}>
                          allocate EAN-13
                        </button>
                      )}
                  </td>
                  <td className="td">
                    <input className="input py-1 w-24 money text-sm" defaultValue={fmt(v.priceMinor)}
                      onBlur={(e) => {
                        try {
                          const minor = parseBhd(e.target.value);
                          if (minor !== v.priceMinor) save.mutate({ id: v.id, body: { priceBhd: e.target.value } });
                        } catch { setError(`"${e.target.value}" is not a valid amount`); }
                      }} />
                    {v.priceMinor === 0 && (
                      <span className="badge bg-red-900/40 text-red-300 ml-2">no price — cannot sell</span>
                    )}
                  </td>
                  <td className="td money text-xs">
                    {canAdjustStock ? (
                      <span className="inline-flex flex-wrap gap-x-3 gap-y-1">
                        {['BH21101', 'BH21102', 'BH21103'].map((sc) => (
                          <span key={sc} className="inline-flex items-center gap-1">
                            <span className="text-gray-500">{sc.slice(-3)}</span>
                            <input type="number" min={0} title={`${v.sku} @ ${sc}`}
                              className="input py-0.5 w-14 text-center text-xs"
                              defaultValue={v.stock?.[sc] ?? 0}
                              onBlur={(e) => {
                                const n = Math.max(0, Math.trunc(Number(e.target.value) || 0));
                                if (n !== (v.stock?.[sc] ?? 0)) {
                                  adjustStock.mutate({ sku: v.sku, storeCode: sc, onHand: n });
                                }
                              }} />
                          </span>
                        ))}
                      </span>
                    ) : (
                      Object.entries(v.stock ?? {}).map(([c, n]) => (
                        <span key={c} className="mr-2">{c}:{n as number}</span>
                      ))
                    )}
                  </td>
                  <td className="td">
                    <span className={`badge ${v.active ? 'bg-emerald-900/40 text-emerald-300' : 'bg-gray-800 text-gray-500'}`}>
                      {v.active ? 'active' : 'off'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-800">
            Prices are VAT-inclusive shelf prices. Edits are audited. Barcodes are allocated
            from the FEMI9 8000001xxxxxx range with a valid check digit.
          </div>
        </div>
      )}
    </div>
  );
}
